import React, { useEffect, useMemo, useState } from 'react';
import { FiClock, FiCreditCard, FiXCircle } from 'react-icons/fi';

type PaymentDueTimerProps = {
  endDate?: string | Date | null;
  daysRemaining?: number | null;
  label?: string;
  className?: string;
  onPay?: () => void;
  payLabel?: string;
  paying?: boolean;
  /** When already paid (active period), cancel instead of pay */
  onCancel?: () => void;
  cancelLabel?: string;
  canceling?: boolean;
  cancelAtPeriodEnd?: boolean;
  onResume?: () => void;
  resumeLabel?: string;
  resuming?: boolean;
  /** e.g. "$99" — amount the user owes */
  amountDue?: string | null;
  /** Shown while subscription endDate has not passed yet */
  hint?: string | null;
  /** Shown during the 2-day grace window after endDate */
  graceHint?: string | null;
  /** Shown after endDate + graceDays (trading paused) */
  overdueHint?: string | null;
  /** Grace days after endDate before trading pauses (default 2) */
  graceDays?: number;
  /**
   * Custom / unpaid Whop grant: show Pay (not Cancel) even if endDate is still in the future.
   */
  requiresPayment?: boolean;
  /** Planned trial length (days) — shown before card is added; clock starts after Whop. */
  plannedTrialDays?: number | null;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function toMs(value?: string | Date | null): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function daysFromNow(targetMs: number, nowMs: number): number {
  return Math.ceil((targetMs - nowMs) / MS_PER_DAY);
}

/** Compact countdown for next master fee / billing payment */
const PaymentDueTimer: React.FC<PaymentDueTimerProps> = ({
  endDate,
  daysRemaining,
  label = 'Master fee due in',
  className = '',
  onPay,
  payLabel = 'Pay now',
  paying = false,
  onCancel,
  cancelLabel = 'Cancel',
  canceling = false,
  cancelAtPeriodEnd = false,
  onResume,
  resumeLabel = 'Resume billing',
  resuming = false,
  amountDue = null,
  hint = null,
  graceHint = null,
  overdueHint = null,
  graceDays = 2,
  requiresPayment = false,
  plannedTrialDays = null,
}) => {
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const endMs = useMemo(() => toMs(endDate), [endDate]);
  const graceEndMs = useMemo(
    () => (endMs == null ? null : endMs + graceDays * MS_PER_DAY),
    [endMs, graceDays]
  );

  const phase = useMemo(() => {
    if (endMs == null) return null;
    const now = nowTick;
    if (now < endMs) return 'active' as const;
    if (graceEndMs != null && now < graceEndMs) return 'grace' as const;
    return 'paused' as const;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endMs, graceEndMs, nowTick]);

  const days = useMemo(() => {
    if (endMs == null || phase == null) return null;
    if (phase === 'active') {
      const fromEnd = Math.max(0, daysFromNow(endMs, nowTick));
      // Pending/unpaid custom plans often send daysRemaining=0 — prefer calendar endDate
      if (
        requiresPayment ||
        daysRemaining == null ||
        !Number.isFinite(daysRemaining) ||
        (Number(daysRemaining) <= 0 && fromEnd > 0)
      ) {
        return fromEnd;
      }
      return Math.max(0, Math.ceil(daysRemaining));
    }
    if (phase === 'grace' && graceEndMs != null) {
      return Math.max(0, daysFromNow(graceEndMs, nowTick));
    }
    return 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endMs, graceEndMs, daysRemaining, phase, nowTick, requiresPayment]);

  if (phase == null || days == null) return null;

  const dueDate = endDate
    ? new Date(endDate).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const graceEndDate =
    graceEndMs != null
      ? new Date(graceEndMs).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : null;

  const inGrace = phase === 'grace';
  const paused = phase === 'paused';
  // Unpaid custom Whop grant is not a paid period — force Pay CTA
  const paidActive = phase === 'active' && !requiresPayment;
  const awaitingPayment = requiresPayment && (phase === 'active' || inGrace || paused);
  const urgent = (paidActive || awaitingPayment) && days <= 7;

  const trialAfterPay =
    plannedTrialDays != null && Number.isFinite(Number(plannedTrialDays))
      ? Math.max(0, Math.ceil(Number(plannedTrialDays)))
      : null;

  const displayLabel = awaitingPayment
    ? 'Add your card to start trading'
    : paused
      ? 'Trading paused'
      : inGrace
        ? 'Grace period — days left'
        : cancelAtPeriodEnd
          ? 'Access ends in'
          : label;

  const statusLine = awaitingPayment
    ? 'Add your card to unlock trading'
    : paused
      ? 'Grace period ended'
      : inGrace
        ? graceEndDate
          ? `Grace ends on ${graceEndDate}`
          : 'Payment overdue — grace period active'
        : dueDate
          ? cancelAtPeriodEnd
            ? `Access until ${dueDate}`
            : paidActive
              ? `Current period ends ${dueDate}`
              : `Due on ${dueDate}`
          : null;

  // Only show grace/overdue copy after the period ends — not while days remain
  const message = awaitingPayment
    ? hint || 'Your trial has not started yet. Add your card to begin trading.'
    : paused
      ? overdueHint
      : inGrace
        ? graceHint || hint
        : cancelAtPeriodEnd
          ? 'Billing is cancelled. You keep access until the period ends, then trading may pause.'
          : null;

  const showPay = (!paidActive || awaitingPayment) && !!onPay;
  const showCancel = paidActive && !cancelAtPeriodEnd && !!onCancel;
  const showResume = paidActive && cancelAtPeriodEnd && !!onResume;

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border px-4 py-3 ${
        paused
          ? 'border-red-500/40 bg-red-500/10'
          : inGrace
            ? 'border-amber-500/40 bg-amber-500/10'
            : urgent
              ? 'border-amber-500/40 bg-amber-500/10'
              : 'border-border bg-surface-elevated'
      } ${className}`}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div
          className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            paused
              ? 'bg-red-500/20 text-red-500'
              : inGrace || urgent
                ? 'bg-amber-500/20 text-amber-500'
                : 'bg-primary/15 text-primary'
          }`}
        >
          <FiClock className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{displayLabel}</p>
          {amountDue && (!paidActive || awaitingPayment) && (
            <p className="mt-0.5 text-sm font-bold text-foreground">
              Amount due:{' '}
              <span className={paused || inGrace || awaitingPayment ? 'text-red-500' : 'text-primary'}>
                {amountDue}
              </span>
            </p>
          )}
          {amountDue && paidActive && !awaitingPayment && (
            <p className="mt-0.5 text-sm font-bold text-foreground">
              Plan:{' '}
              <span className="text-primary">{amountDue}/mo</span>
            </p>
          )}
          {statusLine && <p className="mt-0.5 text-xs text-muted">{statusLine}</p>}
          {message && (
            <p
              className={`mt-1.5 text-xs leading-relaxed max-w-xl ${
                paused ? 'font-semibold text-red-500' : 'text-muted'
              }`}
            >
              {message}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-3 sm:justify-end sm:border-0 sm:pt-0 shrink-0">
        {!awaitingPayment && (
          <div className="flex items-baseline gap-2 sm:block sm:text-right">
            <p
              className={`text-2xl font-bold tabular-nums leading-none ${
                paused ? 'text-red-500' : inGrace || urgent ? 'text-amber-500' : 'text-foreground'
              }`}
            >
              {paused ? 0 : days}
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted sm:mt-1">
              {paused
                ? 'Days left'
                : inGrace
                  ? days === 1
                    ? 'Grace day left'
                    : 'Grace days left'
                  : days === 1
                    ? 'Day left'
                    : 'Days left'}
            </p>
          </div>
        )}
        {showCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={canceling}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-sm font-semibold hover:bg-red-500/15 disabled:opacity-50 shrink-0"
          >
            <FiXCircle className="h-4 w-4" />
            {canceling ? 'Cancelling...' : cancelLabel}
          </button>
        )}
        {showResume && (
          <button
            type="button"
            onClick={onResume}
            disabled={resuming}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-primary/30 bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/15 disabled:opacity-50 shrink-0"
          >
            {resuming ? 'Resuming...' : resumeLabel}
          </button>
        )}
        {showPay && (
          <button
            type="button"
            onClick={onPay}
            disabled={paying}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50 shrink-0 bg-red-600 hover:bg-red-700"
          >
            <FiCreditCard className="h-4 w-4" />
            {paying ? 'Redirecting...' : payLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentDueTimer;
